import SEOHead from '@/components/SEOHead';
import {
  CheckCircleFilled,
  CloudUploadOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Card, Col, Collapse, Row, Spin, Typography, Upload } from 'antd';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useState } from 'react';

const { Dragger } = Upload;
const { Title, Text } = Typography;
const { Panel } = Collapse;

const BimiLogoValidator = () => {
  console.log('BimiLogoValidator: Component initialized');
  console.log('Confetti available:', typeof confetti === 'function');

  const [isLoading, setIsLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    console.log('BimiLogoValidator: Component mounted');
    console.log('Confetti module details:', {
      type: typeof confetti,
      available: !!confetti,
      functionString: confetti?.toString().slice(0, 100),
    });
  }, []);

  const handleLogoUpload = async (file) => {
    console.log('handleLogoUpload: Started with file:', file.name);
    setIsLoading(true);
    setValidationResult(null);
    setLogoPreview(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const svgContent = e.target.result;
        console.log('File read complete, SVG length:', svgContent.length);

        const dataURI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;
        setLogoPreview(dataURI);

        if (!svgContent || typeof svgContent !== 'string') {
          throw new Error('Invalid SVG content');
        }

        console.log('Sending validation request to API...');
        const response = await axios.post(
          '/api/validateBimiLogo',
          { svgContent },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        console.log('API Response received:', {
          status: response.status,
          data: response.data,
        });

        if (response.status === 204) {
          console.log('Logo validation successful, preparing confetti...');
          const result = {
            valid: true,
            message:
              'Congratulations, your logo is BIMI compliant and ready for use. Now add it to your DNS settings.',
          };
          setValidationResult(result);

          // Configure and trigger confetti with better visibility
          const triggerConfetti = () => {
            const defaults = {
              spread: 360,
              ticks: 100,
              gravity: 0,
              decay: 0.94,
              startVelocity: 30,
              shapes: ['star'],
              colors: ['#247bfe', '#52c41a', '#f5222d', '#fa8c16', '#722ed1'],
            };

            function shoot() {
              confetti({
                ...defaults,
                particleCount: 40,
                scalar: 1.2,
                shapes: ['star'],
              });

              confetti({
                ...defaults,
                particleCount: 10,
                scalar: 0.75,
                shapes: ['circle'],
              });
            }

            setTimeout(shoot, 0);
            setTimeout(shoot, 100);
            setTimeout(shoot, 200);
          };

          // Trigger the confetti effect
          console.log('Triggering enhanced confetti...');
          try {
            triggerConfetti();
            console.log('Confetti executed successfully');
          } catch (error) {
            console.error('Error executing confetti:', error);
          }
        } else if (response.data && typeof response.data.valid === 'boolean') {
          console.log('Setting validation result for invalid logo');
          setValidationResult(response.data);
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Error in logo validation:', error);
        let errorMessage = 'An error occurred while validating the logo.';
        if (error.response && error.response.data) {
          if (Array.isArray(error.response.data.errors)) {
            errorMessage = error.response.data.errors.join(' ');
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else {
            errorMessage = JSON.stringify(error.response.data);
          }
        }
        setValidationResult({
          valid: false,
          message: errorMessage,
        });
      } finally {
        setIsLoading(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div>
      <SEOHead />

      <section
        className="products section hero v4"
        style={{ paddingTop: '132px', paddingBottom: '0' }}
      >
        <div className="container-default w-container">
          <div className="z-index-1">
            <div className="mg-bottom-24px">
              <div className="inner-container center">
                <div className="inner-container _861px---tablet center">
                  <div className="text-center">
                    <motion.div
                      className="mg-bottom-24px"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        <p>VALIDATE YOUR BIMI LOGO</p>
                      </div>
                    </motion.div>
                    <motion.h1
                      className="mg-bottom-16px"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                    >
                      BIMI Logo Validator
                    </motion.h1>
                    <div className="inner-container _745px center">
                      <motion.p
                        className="color-neutral-600 mg-bottom-0 paragraph-medium"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                      >
                        Upload your logo to validate it for BIMI compliance. Your logo must be in
                        Scalable Vector Graphic (SVG) format and meet the{' '}
                        <Link
                          href="https://www.digicert.com/blog/getting-ready-for-bimi-prep-your-logo#:~:text=To%20prepare%20for%20acquiring%20a,Vector%20Graphic%20(SVG)%20format."
                          target="_blank"
                        >
                          BIMI SVG requirements
                        </Link>
                        .
                      </motion.p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div
        id="certificate_request_app"
        className="bg-neutral-100"
        style={{ paddingTop: '16px', paddingBottom: '64px' }}
      >
        <div className="container-default w-container">
          <div className="mg-bottom-16px">
            <motion.div
              className="inner-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              style={{ maxWidth: '680px', margin: '0 auto' }}
            >
              <Card
                title={
                  <span style={{ fontSize: '20px' }}>
                    <CloudUploadOutlined style={{ color: '#247bfe', marginRight: '8px' }} />
                    Upload Your Logo
                  </span>
                }
                className="ant-card ant-card-bordered card summary-card"
              >
                <Row gutter={[24, 24]}>
                  <Col xs={24}>
                    <div style={{ textAlign: 'center', maxWidth: '500px', margin: '16px auto' }}>
                      <Dragger
                        name="logo"
                        multiple={false}
                        accept=".svg"
                        beforeUpload={(file) => {
                          handleLogoUpload(file);
                          return false;
                        }}
                        showUploadList={false}
                        style={{
                          padding: '32px',
                          borderWidth: '2px',
                          borderRadius: '16px',
                          backgroundColor: '#fafafa',
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          {isLoading ? (
                            <>
                              <Spin size="large" style={{ marginBottom: '24px' }} />
                              <p className="upload-text">Validating logo...</p>
                            </>
                          ) : (
                            <>
                              <UploadOutlined
                                style={{ fontSize: '64px', marginBottom: '24px', color: '#247bfe' }}
                              />
                              <p className="upload-text">
                                Click or drag your SVG logo file here to validate
                              </p>
                              <p className="upload-hint">Must be an SVG file format</p>
                            </>
                          )}
                        </div>
                      </Dragger>
                    </div>
                  </Col>
                </Row>

                <AnimatePresence mode="wait">
                  <div style={{ textAlign: 'center', marginBottom: '32px', marginTop: '24px' }}>
                    {logoPreview && (
                      <motion.img
                        src={logoPreview}
                        alt="Original Logo"
                        style={{
                          width: '200px',
                          height: '200px',
                          objectFit: 'contain',
                          borderRadius: '0',
                          backgroundColor: 'rgba(229, 231, 235, 0.5)',
                        }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    )}

                    {validationResult && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        style={{
                          textAlign: 'center',
                          marginTop: '32px',
                        }}
                      >
                        {validationResult.valid ? (
                          <>
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="validation-result"
                              style={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                                marginBottom: '24px',
                              }}
                            >
                              <CheckCircleFilled
                                style={{ fontSize: '24px', color: '#05c168', marginRight: '8px' }}
                              />
                              <span style={{ color: '#05c168', fontSize: '16px', fontWeight: 500 }}>
                                {validationResult.message}
                              </span>
                            </motion.div>
                          </>
                        ) : (
                          <>
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="validation-result"
                              style={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                                marginBottom: '24px',
                              }}
                            >
                              <ExclamationCircleOutlined
                                style={{ fontSize: '24px', color: '#ff4d4f', marginRight: '8px' }}
                              />
                              <span style={{ color: '#ff4d4f', fontSize: '16px', fontWeight: 500 }}>
                                {validationResult.message}
                              </span>
                            </motion.div>

                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.2 }}
                              style={{ marginTop: '24px' }}
                            >
                              <Card style={{ textAlign: 'left' }}>
                                <Text strong>BIMI Logo Requirements:</Text>
                                <div style={{ marginTop: '16px', textAlign: 'left' }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      marginBottom: '12px',
                                    }}
                                  >
                                    <div style={{ marginRight: '8px' }}>
                                      <CheckCircleFilled
                                        style={{
                                          fontSize: '24px',
                                          color: '#05c168',
                                          marginRight: '8px',
                                        }}
                                      />
                                    </div>
                                    <span>
                                      It includes the correct XML namespace{' '}
                                      <code>xmlns="http://www.w3.org/2000/svg"</code>
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      marginBottom: '12px',
                                    }}
                                  >
                                    <div style={{ marginRight: '8px' }}>
                                      <CheckCircleFilled
                                        style={{
                                          fontSize: '24px',
                                          color: '#05c168',
                                          marginRight: '8px',
                                        }}
                                      />
                                    </div>
                                    <span>
                                      Dimensions and viewBox are defined correctly to ensure proper
                                      scaling
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      marginBottom: '12px',
                                    }}
                                  >
                                    <div style={{ marginRight: '8px' }}>
                                      <CheckCircleFilled
                                        style={{
                                          fontSize: '24px',
                                          color: '#05c168',
                                          marginRight: '8px',
                                        }}
                                      />
                                    </div>
                                    <span>
                                      No scripts, external resources, or elements that could
                                      compromise security are present
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      marginBottom: '12px',
                                    }}
                                  >
                                    <div style={{ marginRight: '8px' }}>
                                      <CheckCircleFilled
                                        style={{
                                          fontSize: '24px',
                                          color: '#05c168',
                                          marginRight: '8px',
                                        }}
                                      />
                                    </div>
                                    <span>
                                      All inline styles are explicitly defined and kept secure
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      marginBottom: '12px',
                                    }}
                                  >
                                    <div style={{ marginRight: '8px' }}>
                                      <CheckCircleFilled
                                        style={{
                                          fontSize: '24px',
                                          color: '#05c168',
                                          marginRight: '8px',
                                        }}
                                      />
                                    </div>
                                    <span>
                                      The SVG file is sanitized and free of any potentially
                                      malicious code
                                    </span>
                                  </div>
                                </div>
                              </Card>
                            </motion.div>

                            <div style={{ marginTop: '48px', marginBottom: '24px' }}>
                              <Text style={{ fontWeight: 600 }}>
                                Gain instant access to our BIMI Logo AI Wizard and fix your logo
                                with ease.
                              </Text>
                              <div style={{ marginTop: 16 }}>
                                <Link href="/auth/signup" passHref style={{ marginTop: 24 }}>
                                  <Button
                                    type="primary"
                                    size="large"
                                    className="btn-primary"
                                    icon={<UserOutlined />}
                                    style={{
                                      background: 'linear-gradient(90deg, #247bfe, #1a6ed8)',
                                      borderColor: 'transparent',
                                      fontSize: '16px',
                                      height: 'auto',
                                      padding: '12px 32px',
                                      borderRadius: '8px',
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                    }}
                                  >
                                    Create a Free Account Now!
                                  </Button>
                                </Link>
                              </div>
                            </div>
                          </>
                        )}
                      </motion.div>
                    )}
                  </div>
                </AnimatePresence>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        .bimi-logo-validator {
          .upload-text {
            font-size: 16px;
            color: #333;
            margin-bottom: 4px;
          }

          .upload-hint {
            font-size: 14px;
            color: #666;
            margin: 0;
          }

          .ant-card {
            border-radius: 12px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;

            &:hover {
              box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
            }
          }

          .ant-upload-drag {
            transition: all 0.3s ease;

            &:hover {
              border-color: #247bfe;
              background-color: #f8f9fa;
            }
          }

          .requirements-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .requirement-item {
            display: flex;
            align-items: flex-start;
            gap: 12px;

            .check-icon {
              background: rgba(82, 196, 26, 0.1);
              width: 24px;
              height: 24px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }
          }

          code {
            background: rgba(0, 0, 0, 0.04);
            padding: 2px 6px;
            border-radius: 4px;
            font-family: monospace;
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default BimiLogoValidator;
